// PeopleGrid Component - Virtualized grid for displaying cast/crew
import { useVirtualizer } from '@tanstack/react-virtual';
import React from 'react';
import { Link } from 'react-router-dom';
import { useMovieModal } from '../../MovieModalContext';
import { FadeInImage } from '../FadeInImage';

const COLUMN_WIDTH = 350;
const GUTTER = 8;

interface PeopleGridProps {
  people: any[];
  showContent: boolean;
  scrollElement?: React.RefObject<HTMLDivElement>; // Scrollable parent container
}

export function PeopleGrid({ people, showContent, scrollElement }: PeopleGridProps) {
  if (!people || people.length === 0) return null;

  const { openMovie } = useMovieModal();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const getColumnCount = () => {
    if (!containerRef.current) return 1;
    const containerWidth = containerRef.current.clientWidth;
    return Math.max(
      1,
      Math.floor((containerWidth + GUTTER) / (COLUMN_WIDTH + GUTTER)),
    );
  };

  const [columnCount, setColumnCount] = React.useState(1);

  React.useEffect(() => {
    const columns = getColumnCount();
    setColumnCount(columns);

    const handleResize = () => {
      setColumnCount(getColumnCount());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rowCount = Math.ceil(people.length / columnCount);

  const virtualizer = useVirtualizer({
    count: rowCount,
    overscan: 5,
    estimateSize: () => 120,
    getScrollElement: () => scrollElement?.current || document.documentElement,
  });

  return (
    <div
      ref={containerRef}
      className={showContent ? 'fade-in' : ''}
      style={{
        width: '100%',
        opacity: showContent ? 1 : 0,
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const startIndex = virtualItem.index * columnCount;
          const itemsInRow = Math.min(columnCount, people.length - startIndex);

          return (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
                display: 'grid',
                gridTemplateColumns: `repeat(${columnCount}, ${COLUMN_WIDTH}px)`,
                gap: `${GUTTER}px`,
                padding: '12px',
                justifyContent: 'center',
              }}
            >
              {Array.from({ length: itemsInRow }).map((_, i) => {
                const personIndex = startIndex + i;
                const person = people[personIndex];
                return (
                  <div key={personIndex}>
                    <CastAndCrewInfo person={person} openMovie={openMovie} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CastAndCrewInfo({ person, openMovie }: any) {
  return (
    <Link
      className="movie-modal__castItem"
      to={`/p/${person.id}/${person.name}`}
    >
      <div className="movie-modal__castItem__castImageWrapper">
        <FadeInImage
          src={
            person.profile_path
              ? `https://image.tmdb.org/t/p/w185${person.profile_path}`
              : null
          }
          alt={person.name}
          loading="lazy"
          fallbackSrc="/img/noPhoto.webp"
        />
      </div>
      <div className="movie-modal__castInfo">
        <p className="movie-modal__castName">{person.name}</p>
        <p className="movie-modal__castRole">{person.known_for_department}</p>
        {person.known_for
          .filter((item: any) => item.media_type == 'movie')
          .map((m: any) => {
            return (
              <div key={m.id}>
                <div
                  onClick={() => openMovie(m.id)}
                  style={{ cursor: 'pointer', color: 'inherit' }}
                >
                  {m.title} (
                  {m.release_date ? m.release_date.substring(0, 4) : 'N/A'})
                </div>
              </div>
            );
          })}
      </div>
    </Link>
  );
}
